import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';

import { Grid, GridColumn, GridNoRecords, getSelectedState } from '@progress/kendo-react-grid';
import { getter } from "@progress/kendo-react-common";

import LoadingPanel from "views/widgets/LoadingPanel.jsx";
import ColumnMenu from 'views/widgets/ColumnMenu.jsx';

import useSession from '../../hooks/useSession';
import { getHiddenColumns, getInitialColumnState, onColumnsChanged } from 'views/widgets/gridColumnManager';

const PagingGrid = forwardRef((props, ref) => {

	let { columns, rowHeight, hideHeader = false, style, prefKey,
		page, pageSizes, updatePage,
		results, selectionChanged, isSelectable = true, showGrid = true,
		handleContextMenu, detailView, expandedChanged, 
		selectedState, setSelectedState=()=>{}, selectionMode,
		checkboxColumnWidth = "50px", inEdit = false, onItemChange } = props;
		
	const { getPreference, setPreference } = useSession();
	
	let dataState = results.data.map((dataItem) =>
		Object.assign({
			selected: false,
		},
			dataItem
		));
	const DATA_ITEM_KEY = "id";
	const SELECTED_FIELD = "selected";
	const EXPANDED_FIELD = "expanded";
	const idGetter = getter(DATA_ITEM_KEY);
	const [isFilterable, setIsFilterable] = useState(false);
	const [filteredDataState, setFilteredDataState]= useState({ sort: page.sort, filters: [] });	
	const [columnState, setColumnState] = useState(getInitialColumnState(columns, getPreference, prefKey));
	const [hiddenColumns, setHiddenColumns] = useState(getHiddenColumns(getPreference, prefKey)); 
	const [resettingGrid, setResettingGrid] = useState(false);
	const columnStateRef = useRef(columnState);
	
	const updateColumnState = () => {
		const newColumnState = showGrid ? getInitialColumnState(columns, getPreference, prefKey) : columns;
		columnStateRef.current = newColumnState;
		setColumnState(newColumnState);
	};
	
	const updateHiddenColumns = (newHiddenColumns) => {
		setPreference(prefKey + "_HIDDEN_COLUMNS", JSON.stringify(newHiddenColumns));
		setHiddenColumns(newHiddenColumns);
		setColumnState(getInitialColumnState(columns, getPreference, prefKey));
	};
	
	useEffect(() => {
		const pageElems = document.querySelectorAll('.k-pager-sizes .k-dropdownlist');
		pageElems.forEach((elem) => {
			elem.setAttribute("aria-label", "Select page size");
		});
	}, []);
	
	useEffect(() => {
		updateColumnState();
	}, [ columns, showGrid ]);
	
	useImperativeHandle(ref, () => ({
		restoreDefaultLayout() {
			restoreLayout();
		}
	}));
	
	const restoreLayout = () => {
		setPreference(prefKey, null);
		setPreference(prefKey + "_DATA_STATE", null);
		updateColumnState();
		updateHiddenColumns([]);
		setResettingGrid(true);
		setTimeout(() => {
			setResettingGrid(false);
		}, 0)		
	};

	const updateCellTooltip = (event) => {
		const cell = event.target.closest?.("td.k-table-td");

		if (!cell || !event.currentTarget.contains(cell)) return;

		const fullText = cell.textContent?.trim() ?? "";
		if (fullText) {
			cell.title = fullText;
		} else {
			cell.removeAttribute("title");
		}
	};

	const customCellRender = (column, gridItem) => {
		const onClick = column.card ? () => { } : () => customCellClicked(gridItem);
		const tdProps = gridItem.tdProps || {};

		return (
			<td
				{...tdProps}
				className={`k-table-td ${tdProps.className || ""}`.trim()}
				onClick={onClick}
				onContextMenu={(e) => {
					handleContextMenu({
						syntheticEvent: e,
						dataItem: gridItem.dataItem,
						field: gridItem.field
					});
				}}
			>
				{column.cell(gridItem)}
			</td>
		);
	};
	
	const customCellClicked = (gridItem) => {
		const id = gridItem.dataItem.id;

		let newSelectionState;

		if ((selectionMode || "single") === "multiple") {
			newSelectionState = {
				...selectedState,
				[id]: !selectedState[id]
			};
		} else {
			newSelectionState = {
				[id]: true
			};
		}

		setSelectedState(newSelectionState);
		selectionChanged(newSelectionState);
	};

	// *** event handlers
	const onSelectionChange = useCallback(
		(event) => {
			const newSelectedState = event.select;

			setSelectedState(newSelectedState);
			selectionChanged(newSelectedState);
		},
		[]
	);
		
	const onHeaderSelectionChange = useCallback((event) => {		
		const checkboxElement = event.syntheticEvent.target;
		const checked = checkboxElement.checked;
		const newSelectedState = {};
		event.dataItems.forEach((item) => {
			newSelectedState[idGetter(item)] = checked;
		});
		setSelectedState(newSelectedState);
		selectionChanged(newSelectedState);
	}, []);

	const sortChanged = (e) => {		
		// must be reversing direction on the current sort
		if (e.sort === undefined || e.sort.length === 0 || e.sort[0].field === page.sort[0].field)
			page.sort = [{ field: page.sort[0].field, dir: page.sort[0].dir === "asc" ? "desc" : "asc" }];
		else
			page.sort = e.sort;
		updatePage(page);
		let newFilteredDataState = { ...filteredDataState, sort: page.sort }; 
		setFilteredDataState(newFilteredDataState);
		setPreference(prefKey + "_DATA_STATE", JSON.stringify(newFilteredDataState));
	};

	const onPageChange = (event) => {
		updatePage(event.page);
	};

	const onExpandChange = (event) => {
		expandedChanged(event);
	};
	
	const saveColumnState = (newColumnState) => {
		columnStateRef.current = newColumnState;
		setPreference(prefKey, JSON.stringify(newColumnState));
		return newColumnState;
	};
	
	const onColumnReorder = (event) => {
		const orderByField = {};

		(event.columns || [])
			.filter((column) => column.field !== SELECTED_FIELD)
			.forEach((column) => {
				orderByField[column.field] = column.orderIndex;
			});

		const newColumnState = [...columnStateRef.current]
			.map((column) => ({
				...column,
				orderIndex: orderByField[column.field] ?? column.orderIndex
			}))
			.sort((a, b) => a.orderIndex - b.orderIndex)
			.map((column, index) => ({
				...column,
				orderIndex: index + 1
			}));

		saveColumnState(newColumnState);
	};
	
	const onColumnResize = (event) => {
		const visualColumns = [...(event.columns || [])]
			.sort((a, b) => (a.orderIndex ?? 9999) - (b.orderIndex ?? 9999));

		const resizedColumn = visualColumns[event.index];
		const resizedField = resizedColumn?.field;

		if (!resizedField || resizedField === SELECTED_FIELD) return;

		const newColumnState = columnStateRef.current.map((column) =>
			column.field === resizedField
				? { ...column, width: event.newWidth }
				: column
		);

		saveColumnState(newColumnState);
	};
	
	// *** build grid details
	const FILLER_FIELD = "__filler";

	const addCheckbox = () => (
		<GridColumn
			columnType="checkbox"
			field={SELECTED_FIELD}
			width={checkboxColumnWidth}
			headerSelectionValue={
				dataState.length > 0 &&
				dataState.findIndex((item) => !selectedState[idGetter(item)]) === -1
			}
			filterable={false}
			reorderable={false}
			resizable={false}
			disableReorder={true}
		/>
	);

	const addColumns = () => {
		const gridColumns = columnState.map((column) => {

			const filterType = column.field.includes("_date") ? "date" : "text";
			const className = "constituo-" + (column.align ? column.align : 'left') + "-align-column"; 

			return (
				<GridColumn
					key={column.field}
					headerClassName={className}
					className={className}
					field={column.field}
					width={column.width}
					resizable={column.resizable !== false}
					reorderable={column.reorderable !== false}
					sortable={column.sortable !== false}
					title={column.title}
					cells={column.cell ? {
						data: (dataItem) => customCellRender(column, dataItem)
					} : undefined}
					filterable={isFilterable}
					filter={filterType}
					editor={inEdit ? "text" : undefined}
					editable={inEdit}
					columnMenu={(props) => (
						<ColumnMenu
							{...props}
							columns={columns}
							hiddenColumns={hiddenColumns}
							onHiddenColumnsChanged={updateHiddenColumns}
							onRestoreClicked={restoreLayout}
						/>		
					)}
				/>
			);
		});
		//console.log('gridColumns', gridColumns);
		return gridColumns;
	};

	const getResults = () => {
		let data;

		if (showGrid) {
			data = results.data.map((item) => ({
				...item,
				[SELECTED_FIELD]: selectedState[idGetter(item)],
				inEdit: inEdit, //set this to true to edit a field
			}));

		} else {
			data = results.data;
			data.map(dataItem => dataItem.buttons = results.buttons);
		}

		return data;
	}
	
	const gridWidth =
		(isSelectable && selectionMode === "multiple" ? checkboxColumnWidth : 0) +
		columnState.reduce((total, column) => total + (column.width ?? 150), 0);
	
	if (resettingGrid) 
		return <LoadingPanel />
	else
		return (
			<div
				className={`constituo-grid-region ${hideHeader ? "constituo-paging-grid-container-with-no-header" : "constituo-paging-grid-container"}`}
				style={style}
				onMouseOver={updateCellTooltip}
			>
				<Grid
					onContextMenu={handleContextMenu}
					className={showGrid ? "constituo-paging-grid" : "ccm-paging-grid-no-border"}
	
					// grid selection
					selectedField={SELECTED_FIELD}
					selectable={{
						enabled: isSelectable,
						drag: false,
						cell: false,
						mode: selectionMode || "single"
					}}
					
					onSelectionChange={onSelectionChange}
					onHeaderSelectionChange={onHeaderSelectionChange}
	
					// grid detail
					expandField={EXPANDED_FIELD}
					onExpandChange={onExpandChange}
					detail={detailView}
					/*detail={DetailComponent}*/
	
					// grid sorting				    	
					sortable={{
						allowUnsort: true,
						mode: "single"
					}}
					sort={page.sort}
					onSortChange={sortChanged}
					editField="inEdit"
					onItemChange={onItemChange}
					
					// grid paging
					skip={page.skip}
					take={page.take}
					total={results.total}
					pageable={{ 
						buttonCount: 5,
						info: true,
						type: "numeric",
						previousNext: true,
						pageSizes: pageSizes,
						navigatable: true
					}}
					onPageChange={onPageChange}
	
					// grid styling								
					style={{ minHeight: 0, height: "100%", width: "auto" }}
					rowHeight={rowHeight}
	
					// grid data
					data={getResults()}
					dataItemKey={DATA_ITEM_KEY}
					resizable={true}
					onColumnResize={onColumnResize}
					reorderable={true}
					onColumnReorder={onColumnReorder}
					navigatable={true} 
				>
					{!showGrid && (
					<GridNoRecords>
						<div/>
					</GridNoRecords>						
					)}
					{isSelectable && selectionMode === "multiple" && addCheckbox()}
					{addColumns()}
				</Grid>
			</div>
		);
});

export default PagingGrid;
